import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, type Location } from 'react-router'
import { LOGIN_NOTICE, isKnownRole, useGetSessionQuery } from '@/entities/session'
import { LogoutSplash, useLogout } from '@/features/auth-logout'
import { isSessionEnded, normalizeError } from '@/shared/api'
import { AccessProvider, withReturnTo } from '@/shared/lib'
import { Alert } from '@/shared/ui/Alert'
import { Button } from '@/shared/ui/Button'
import { PageHeader } from '@/shared/ui/PageHeader'
import { Spinner } from '@/shared/ui/Spinner'
import { AuthBrand, AuthShell } from '@/widgets/auth-shell'
import s from './SessionGate.module.css'

/**
 * Что видно, пока едет `/auth/me`.
 *
 * Не каркас кабинета со скелетонами: сам каркас зависит от прав — меню собирается через
 * `can()`, и скелетон нарисовал бы разделы, которые через мгновение пришлось бы убрать.
 * Поэтому до ответа — splash: одна строка состояния и ничего, что зависит от доступов.
 *
 * Живая область — контейнер: спиннер без подписи декоративен по своему контракту.
 */
function SessionSplash() {
  const { t } = useTranslation()

  return (
    <div className={s.splash} role="status" aria-live="polite">
      <Spinner size="lg" />
      <p className={s.splashText}>{t('session.loading')}</p>
    </div>
  )
}

/**
 * `/auth/me` не ответила (не `401`, а сбой). Кабинет не открывается вовсе, и это осознанное
 * решение: пустой список прав молча спрятал бы половину действий, а «разрешить всё до ответа»
 * нарисовало бы кнопки, которые бэк отвергнет. Поэтому тупик с одной кнопкой — повторить.
 * Марка та же, что у остальных экранов до входа.
 */
function SessionFailure({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  const { t } = useTranslation()
  const { t: ta } = useTranslation('auth')

  // Сырое сообщение сервера в интерфейс не выносим: только ключ локали (§4.5)
  const failure = normalizeError(error)
  const reason = failure.code === 'NETWORK' ? t('errors.network') : t('errors.unknown')

  return (
    <AuthShell
      brand={<AuthBrand name={ta('brand.name')} kind={ta('brand.kind')} />}
      heading={t('session.failedHeading')}
      description={t('session.failedDescription')}
    >
      <PageHeader
        level={2}
        variant="section"
        heading={t('session.failedTitle')}
        description={t('session.failedHint')}
      />

      <Alert tone="danger" announce="assertive">
        {reason}
      </Alert>

      <Button block size="lg" onClick={onRetry}>
        {t('action.retry')}
      </Button>
    </AuthShell>
  )
}

/**
 * Вход с адресом возврата. Сеанс кончился посреди работы (P-AUTH-02#R-06) — ещё и с плашкой
 * «Сеанс завершён» (U-11). Первый заход без сеанса и выход её не получают (R-02): об этом
 * помнит хранилище токена, пока не сменилось поколение сеанса.
 */
function loginPath(location: Location): string {
  const path = withReturnTo('/login', location)
  if (!isSessionEnded()) return path
  return `${path}${path.includes('?') ? '&' : '?'}notice=${LOGIN_NOTICE.sessionEnded}`
}

/**
 * Роль, под которую у фронта нет кабинета (бэк просит `switch` по роли с веткой по умолчанию,
 * `24-frontend-readiness.md` §1). Кабинет не открывается: угадывать состав по чужой роли нельзя.
 * Выход — единственное действие.
 */
function AccessNotConfigured() {
  const { t } = useTranslation()
  const { t: ta } = useTranslation('auth')
  const { logout, isLoggingOut } = useLogout()

  return (
    <AuthShell
      brand={<AuthBrand name={ta('brand.name')} kind={ta('brand.kind')} />}
      heading={t('session.notConfiguredHeading')}
      description={t('session.notConfiguredDescription')}
    >
      <Alert tone="warning">{t('session.notConfiguredHint')}</Alert>

      <Button block size="lg" variant="secondary" onClick={() => void logout()}>
        {ta('account.logout')}
      </Button>

      {isLoggingOut ? <LogoutSplash /> : null}
    </AuthShell>
  )
}

type SessionGateProps = {
  children: ReactNode
}

/**
 * Ворота сеанса вокруг маршрутов кабинета: пока прав нет — кабинета нет.
 *
 * - сеанса нет (`401` после неудачного обновления токена) — вход с адресом возврата, а если
 *   сеанс кончился посреди работы — ещё и с плашкой «Сеанс завершён»;
 * - роль незнакома фронту — «Доступ не настроен» с выходом;
 * - сбой сервера — тупик с «Повторить»;
 * - иначе права ложатся в `AccessProvider`, и маршрут открывается.
 *
 * Порядок веток важен. `401` проверяется раньше данных: в кеше мог остаться прежний ответ, но
 * по правам потерянного сеанса кабинет не открывается. Остальные сбои при уже полученных
 * данных экран не трогают — при фоновом обновлении права не мигают.
 */
export function SessionGate({ children }: SessionGateProps) {
  const { data, isError, error, refetch } = useGetSessionQuery()
  const location = useLocation()

  if (isError && normalizeError(error).status === 401) {
    return <Navigate to={loginPath(location)} replace />
  }

  if (data !== undefined) {
    /*
     * Активного юрлица может не быть (`legalEntity: null`) — у менеджера в том числе. Бэк при
     * входе сам активирует юрлицо, если оно есть, поэтому выбор компании здесь ничего не даст:
     * кабинет открывается, подписи компании нет (`docs/roles/06-rollout.md`, D-07).
     */
    if (!isKnownRole(data.role)) return <AccessNotConfigured />

    return <AccessProvider permissions={data.permissions}>{children}</AccessProvider>
  }

  if (isError) {
    return (
      <SessionFailure
        error={error}
        onRetry={() => {
          void refetch()
        }}
      />
    )
  }

  return <SessionSplash />
}
